import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import { noAutofill } from '../noAutofill';

type Product = {
  id: string;
  asset_id: string;
  name: string;
  category: string;
  price: number;
  image_url: string;
  description: string;
  user_name: string;
  product_url: string;
  created_at: string;
  preorder_count: number;
};

type EventInfo = {
  slug: string;
  name: string;
};

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 300;

const styles = `
.preorder-app {
  color-scheme: light;
  --cap-blue: #0070ad;
  --cap-blue-dark: #004b7a;
  --cap-cyan: #12abdb;
  --cap-cyan-soft: #e6f6fc;
  --ink: #1a1f2c;
  --ink-soft: #5a6473;
  --line: #e3e8ef;
  --bg: #f6f8fb;
  --card: #ffffff;
  --success: #2f9e44;
  --shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06);
  --shadow-md: 0 4px 12px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04);

  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  /* Hard guarantee: this page is never horizontally scrollable, no matter
     what a child element does. Necessary on mobile because flex children
     can still report intrinsic widths wider than the viewport. */
  overflow-x: hidden;
  background: var(--bg);
  color: var(--ink);
  font-family: 'Syne', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.preorder-app,
.preorder-app * { box-sizing: border-box; }

.preorder-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--card);
  border-bottom: 1px solid var(--line);
  backdrop-filter: blur(8px);
}

.preorder-header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  overflow: hidden;
}

.preorder-brand {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex-shrink: 1;
}

.preorder-brand .divider {
  width: 1px;
  height: 28px;
  background: var(--line);
  flex-shrink: 0;
}

.preorder-brand img {
  height: 22px;
  width: auto;
  display: block;
  flex-shrink: 0;
}

.preorder-brand img.sp-logo { height: 26px; }

.preorder-event {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 0;
  flex-shrink: 1;
}

.preorder-event-label {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-soft);
  font-weight: 600;
}

.preorder-event-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preorder-hero {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 8px;
  width: 100%;
}

.preorder-hero h1 {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 6px;
  letter-spacing: -0.01em;
}

.preorder-hero p {
  margin: 0;
  color: var(--ink-soft);
  font-size: 15px;
}

.preorder-toolbar {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 20px 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
}

.preorder-search {
  flex: 1 1 280px;
  min-width: 240px;
  position: relative;
  display: flex;
  align-items: center;
  max-width: 100%;
}

.preorder-search input {
  width: 100%;
  max-width: 100%;
  height: 44px;
  padding: 0 14px 0 42px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--card);
  font-size: 15px;
  font-family: inherit;
  color: var(--ink);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}

.preorder-search input::placeholder { color: #9aa3b2; }

.preorder-search input:focus {
  outline: none;
  box-shadow: none;
}

.preorder-search-icon {
  position: absolute;
  left: 14px;
  width: 18px;
  height: 18px;
  color: #6b7480;
  pointer-events: none;
}

.preorder-result-count {
  font-size: 13px;
  color: var(--ink-soft);
  white-space: nowrap;
}

.preorder-filters {
  max-width: 1200px;
  margin: 0 auto;
  padding: 6px 20px 4px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.preorder-filters::-webkit-scrollbar { display: none; }

.preorder-chip {
  flex-shrink: 0;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--card);
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
}

.preorder-chip:hover {
  border-color: var(--cap-cyan);
  color: var(--cap-blue);
}

.preorder-chip.active {
  background: var(--cap-blue);
  border-color: var(--cap-blue);
  color: #fff;
}

.preorder-grid {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 20px 80px;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 18px;
}

.preorder-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-sm);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.preorder-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: #d4dae3;
}

.preorder-card-link {
  color: inherit;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.preorder-card-link:focus-visible {
  outline: 2px solid var(--cap-cyan);
  outline-offset: -2px;
  border-radius: 14px;
}

.preorder-card-media {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: linear-gradient(135deg, #f1f4f8 0%, #e9eef4 100%);
  overflow: hidden;
}

.preorder-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.preorder-card-link:hover .preorder-card-media img {
  transform: scale(1.03);
}

.preorder-card-media .external-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: var(--cap-blue-dark);
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.12);
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
}

.preorder-card-link:hover .preorder-card-media .external-icon,
.preorder-card-link:focus-visible .preorder-card-media .external-icon {
  opacity: 1;
  transform: translateY(0);
}

.preorder-card-media .placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa3b2;
  font-size: 13px;
}

.preorder-card-body {
  padding: 14px 16px 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-grow: 1;
}

.preorder-card-footer {
  padding: 4px 16px 16px;
}

.preorder-card-category {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--cap-blue);
}

.preorder-card-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.preorder-card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}

.preorder-card-price {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}

.preorder-card-author {
  font-size: 12px;
  color: var(--ink-soft);
  max-width: 50%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preorder-card-actions {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.preorder-count {
  flex: 0 0 auto;
  min-width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 10px;
  background: var(--cap-cyan-soft);
  color: var(--cap-blue-dark);
}

.preorder-count-num {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}

.preorder-count-num.bump { animation: countBump 0.4s ease; }

.preorder-count-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
  margin-top: 3px;
  opacity: 0.8;
}

.preorder-btn {
  flex: 1;
  height: auto;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  border: none;
  background: var(--cap-blue);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.preorder-btn:hover:not(:disabled) { background: var(--cap-blue-dark); }
.preorder-btn:active:not(:disabled) { transform: scale(0.98); }

.preorder-btn:disabled { cursor: default; }

.preorder-btn.loading { opacity: 0.7; cursor: progress; }

.preorder-skeleton {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 14px;
  overflow: hidden;
}

.preorder-skeleton .skeleton-media {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: linear-gradient(90deg, #eef1f5 25%, #f6f8fb 50%, #eef1f5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
}

.preorder-skeleton .skeleton-line {
  height: 12px;
  margin: 10px 16px;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef1f5 25%, #f6f8fb 50%, #eef1f5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
}

.preorder-skeleton .skeleton-line.short { width: 40%; }
.preorder-skeleton .skeleton-line.medium { width: 70%; }

.preorder-empty {
  max-width: 480px;
  margin: 48px auto;
  text-align: center;
  color: var(--ink-soft);
  padding: 32px;
}

.preorder-empty h3 { margin: 0 0 6px; color: var(--ink); font-weight: 600; }
.preorder-empty p { margin: 0; font-size: 14px; }

.preorder-error {
  max-width: 540px;
  margin: 48px auto;
  padding: 24px;
  text-align: center;
  border: 1px solid #ffd9d9;
  background: #fff5f5;
  border-radius: 12px;
  color: #842029;
}

.preorder-load-sentinel {
  height: 1px;
  width: 100%;
}

.preorder-loading-more {
  grid-column: 1 / -1;
  text-align: center;
  padding: 12px 0 24px;
  color: var(--ink-soft);
  font-size: 13px;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

@keyframes countBump {
  0% { transform: scale(1); }
  35% { transform: scale(1.25); color: var(--success); }
  100% { transform: scale(1); }
}

@media (max-width: 640px) {
  /* Header is the most contested row on mobile: two logos, a divider, and
     the event name all have to fit on a 320–390px viewport without
     introducing horizontal scroll. Shrink everything and let the brand /
     event blocks compress with ellipsis. */
  .preorder-header-inner {
    padding: 10px 12px;
    gap: 8px;
  }
  .preorder-brand { gap: 10px; }
  .preorder-brand .divider { height: 22px; }
  .preorder-brand img { height: 14px; }
  .preorder-brand img.sp-logo { height: 18px; }
  .preorder-event-name {
    font-size: 12px;
    max-width: 130px;
  }
  .preorder-event-label { font-size: 10px; }
}

@media (max-width: 380px) {
  /* On the smallest phones we drop the divider and tighten further so the
     event name still has at least ~80px of room before truncating. */
  .preorder-brand .divider { display: none; }
  .preorder-brand { gap: 8px; }
  .preorder-event-name { max-width: 100px; }
  .preorder-hero { padding: 20px 16px 4px; }
  .preorder-hero h1 { font-size: 22px; }
  .preorder-hero p { font-size: 14px; }
  .preorder-toolbar { padding: 14px 16px 4px; gap: 8px; }
  /* On phones, give search the full row so the product count drops to a
     second line instead of being pushed off the right edge. */
  .preorder-search {
    flex: 1 1 100%;
    min-width: 0;
  }
  .preorder-result-count {
    width: 100%;
    white-space: normal;
  }
  /* Let category chips wrap to multiple rows instead of horizontal scrolling. */
  .preorder-filters {
    padding: 6px 16px 4px;
    overflow-x: visible;
    flex-wrap: wrap;
    row-gap: 8px;
  }
  /* One product per row on mobile so each card has room to breathe. */
  .preorder-grid {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 12px 16px 100px;
  }
  /* Slightly shorter image on phones so the call-to-action stays close to
     the thumb without the user having to scroll past a tall hero image. */
  .preorder-card-media { aspect-ratio: 3 / 2; }
}
`;

const SearchIcon = () => (
  <svg
    className="preorder-search-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const HeartIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
  </svg>
);

const formatPrice = (n: number): string => {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n >= 100 ? 0 : 2,
  });
};

type ProductCardProps = {
  product: Product;
  bump: boolean;
  voting: boolean;
  onVote: (productId: string) => void;
};

const ProductCard: React.FC<ProductCardProps> = ({ product, bump, voting, onVote }) => {
  const [imgError, setImgError] = useState(false);

  const productLink = product.product_url || null;

  const Media = (
    <div className="preorder-card-media">
      {product.image_url && !imgError ? (
        <img
          src={product.image_url}
          alt={product.name || 'Product image'}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="placeholder">No image</div>
      )}
      {productLink && (
        <span className="external-icon" aria-hidden>
          <ExternalLinkIcon />
        </span>
      )}
    </div>
  );

  const Body = (
    <div className="preorder-card-body">
      {product.category && (
        <div className="preorder-card-category">{product.category}</div>
      )}
      <div className="preorder-card-name" title={product.name}>
        {product.name || 'Untitled product'}
      </div>
      <div className="preorder-card-meta">
        <span className="preorder-card-price">{formatPrice(product.price)}</span>
        {product.user_name && (
          <span className="preorder-card-author" title={`Designed by ${product.user_name}`}>
            by {product.user_name}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <article className="preorder-card">
      {productLink ? (
        <a
          className="preorder-card-link"
          href={productLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View ${product.name || 'product'} on Shopify`}
        >
          {Media}
          {Body}
        </a>
      ) : (
        <div className="preorder-card-link">
          {Media}
          {Body}
        </div>
      )}
      <div className="preorder-card-footer">
        <div className="preorder-card-actions">
          <div className="preorder-count" aria-label="Pre-order count">
            <span className={`preorder-count-num${bump ? ' bump' : ''}`}>
              {product.preorder_count.toLocaleString()}
            </span>
            <span className="preorder-count-label">Pre-orders</span>
          </div>
          <button
            className={`preorder-btn${voting ? ' loading' : ''}`}
            onClick={() => onVote(product.id)}
            disabled={voting}
            aria-label="Pre-order this product"
          >
            <HeartIcon filled={false} />
            {voting ? 'Saving…' : 'Pre-order'}
          </button>
        </div>
      </div>
    </article>
  );
};

const Skeleton = () => (
  <div className="preorder-skeleton" aria-hidden>
    <div className="skeleton-media" />
    <div className="skeleton-line medium" />
    <div className="skeleton-line short" />
    <div className="skeleton-line medium" />
  </div>
);

const PreorderApp: React.FC = () => {
  const eventSlug = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('event') || '').trim();
  }, []);

  const [event, setEvent] = useState<EventInfo | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [votingIds, setVotingIds] = useState<Set<string>>(new Set());
  const [bumpedIds, setBumpedIds] = useState<Set<string>>(new Set());

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const fetchTokenRef = useRef<number>(0);
  const wsRef = useRef<WebSocket | null>(null);
  const wsRetryTimeoutRef = useRef<number | null>(null);
  const bumpTimeoutsRef = useRef<Map<string, number>>(new Map());

  // Debounce search input -> actual filter value
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const loadProducts = useCallback(
    async (opts: { pageToLoad: number; reset: boolean }) => {
      if (!eventSlug) {
        setError('Missing event. Please open the page from the event QR code.');
        setLoading(false);
        return;
      }

      const token = ++fetchTokenRef.current;

      if (opts.reset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      try {
        const params = new URLSearchParams();
        params.set('page', String(opts.pageToLoad));
        params.set('page_size', String(PAGE_SIZE));
        if (search) params.set('search', search);
        if (selectedCategory) params.set('category', selectedCategory);

        const resp = await fetch(
          `${API_URL}/api/preorder/event/${encodeURIComponent(eventSlug)}?${params.toString()}`,
        );

        if (token !== fetchTokenRef.current) return;

        if (resp.status === 404) {
          setError("We couldn't find this event. Double-check the QR code or contact your organizer.");
          setProducts([]);
          setHasMore(false);
          setTotal(0);
          return;
        }

        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }

        const data = await resp.json();

        if (token !== fetchTokenRef.current) return;

        if (data?.event) setEvent(data.event);

        const incoming: Product[] = Array.isArray(data?.products) ? data.products : [];

        setProducts((prev) => {
          if (opts.reset) return incoming;
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...incoming.filter((p) => !seen.has(p.id))];
        });
        setHasMore(Boolean(data?.has_more));
        setTotal(Number(data?.total ?? 0));
        setPage(opts.pageToLoad);
      } catch (err) {
        if (token !== fetchTokenRef.current) return;
        console.error('Failed to load preorder products', err);
        setError('Could not load products right now. Please try again in a moment.');
      } finally {
        if (token === fetchTokenRef.current) {
          if (opts.reset) setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [eventSlug, search, selectedCategory],
  );

  // Load categories once we know the event
  useEffect(() => {
    if (!eventSlug) return;
    let active = true;
    (async () => {
      try {
        const resp = await fetch(
          `${API_URL}/api/preorder/event/${encodeURIComponent(eventSlug)}/categories`,
        );
        if (!resp.ok) return;
        const data = await resp.json();
        if (active && Array.isArray(data?.categories)) {
          setCategories(data.categories);
        }
      } catch (err) {
        console.warn('Failed to load categories', err);
      }
    })();
    return () => {
      active = false;
    };
  }, [eventSlug]);

  // Reload products whenever filters change
  useEffect(() => {
    if (!eventSlug) {
      setLoading(false);
      setError('Missing event. Please open the page from the event QR code.');
      return;
    }
    loadProducts({ pageToLoad: 1, reset: true });
  }, [eventSlug, search, selectedCategory, loadProducts]);

  // Infinite scroll sentinel
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
          loadProducts({ pageToLoad: page + 1, reset: false });
        }
      },
      { rootMargin: '300px 0px' },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, loadProducts]);

  const triggerBump = useCallback((productId: string) => {
    setBumpedIds((prev) => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
    const existing = bumpTimeoutsRef.current.get(productId);
    if (existing) window.clearTimeout(existing);
    const handle = window.setTimeout(() => {
      setBumpedIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      bumpTimeoutsRef.current.delete(productId);
    }, 500);
    bumpTimeoutsRef.current.set(productId, handle);
  }, []);

  // WebSocket for live updates
  useEffect(() => {
    if (!eventSlug) return;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;

      const apiUrl = new URL(API_URL);
      const proto = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${proto}//${apiUrl.host}/api/preorder/ws/${encodeURIComponent(eventSlug)}`;

      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrl);
      } catch (err) {
        console.warn('Preorder WS init failed', err);
        scheduleReconnect();
        return;
      }

      wsRef.current = ws;

      ws.onopen = () => {
        /* connected; nothing else to track */
      };

      ws.onmessage = (event) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(event.data);
          if (msg?.type === 'preorder_update' && msg.product_id) {
            const pid = String(msg.product_id);
            const newCount = Number(msg.count);
            setProducts((prev) => {
              let changed = false;
              const next = prev.map((p) => {
                if (p.id === pid && p.preorder_count !== newCount) {
                  changed = true;
                  return { ...p, preorder_count: newCount };
                }
                return p;
              });
              return changed ? next : prev;
            });
            triggerBump(pid);
          }
        } catch (err) {
          /* ignore non-JSON */
        }
      };

      ws.onerror = () => {
        /* handled via onclose */
      };

      ws.onclose = () => {
        if (cancelled) return;
        wsRef.current = null;
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      if (wsRetryTimeoutRef.current) window.clearTimeout(wsRetryTimeoutRef.current);
      wsRetryTimeoutRef.current = window.setTimeout(connect, 3000);
    };

    connect();

    return () => {
      cancelled = true;
      if (wsRetryTimeoutRef.current) {
        window.clearTimeout(wsRetryTimeoutRef.current);
        wsRetryTimeoutRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
  }, [eventSlug, triggerBump]);

  // Cleanup bump timeouts on unmount
  useEffect(() => {
    return () => {
      bumpTimeoutsRef.current.forEach((handle) => window.clearTimeout(handle));
      bumpTimeoutsRef.current.clear();
    };
  }, []);

  const handleVote = useCallback(
    async (productId: string) => {
      // Block only while a request for this product is in-flight; users are
      // allowed to vote for the same product as many times as they want.
      if (votingIds.has(productId)) return;
      if (!eventSlug) return;

      setVotingIds((prev) => {
        const next = new Set(prev);
        next.add(productId);
        return next;
      });

      const previousCount =
        products.find((p) => p.id === productId)?.preorder_count ?? 0;
      const optimisticCount = previousCount + 1;

      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, preorder_count: optimisticCount } : p,
        ),
      );
      triggerBump(productId);

      try {
        const resp = await fetch(`${API_URL}/api/preorder/vote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event: eventSlug, product_id: productId }),
        });

        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }

        const data = await resp.json();
        const serverCount = Number(data?.count);

        if (Number.isFinite(serverCount)) {
          setProducts((prev) =>
            prev.map((p) =>
              p.id === productId ? { ...p, preorder_count: serverCount } : p,
            ),
          );
        }
      } catch (err) {
        console.error('Pre-order failed', err);
        // Revert optimistic update
        setProducts((prev) =>
          prev.map((p) =>
            p.id === productId ? { ...p, preorder_count: previousCount } : p,
          ),
        );
      } finally {
        setVotingIds((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }
    },
    [eventSlug, products, votingIds, triggerBump],
  );

  const eventLabel = event?.name || (eventSlug ? eventSlug : 'Event');

  return (
    <div className="preorder-app">
      <style>{styles}</style>

      <header className="preorder-header">
        <div className="preorder-header-inner">
          <div className="preorder-brand">
            <img src="/Capgemini_logo.svg" alt="Capgemini" />
            <span className="divider" aria-hidden />
            <img src="/superpossible_logo.svg" alt="SuperPossible" className="sp-logo" />
          </div>
          <div className="preorder-event">
            <span className="preorder-event-label">Event</span>
            <span className="preorder-event-name" title={eventLabel}>
              {eventLabel}
            </span>
          </div>
        </div>
      </header>

      <section className="preorder-hero">
        <h1>Vote for the products you love</h1>
        <p>Tap pre-order on your favorites. Live counts update in real time.</p>
      </section>

      <div className="preorder-toolbar">
        <div className="preorder-search">
          <SearchIcon />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, designer, or description"
            aria-label="Search products"
            {...noAutofill}
          />
        </div>
        {!loading && !error && (
          <span className="preorder-result-count">
            {total.toLocaleString()} {total === 1 ? 'product' : 'products'}
          </span>
        )}
      </div>

      {categories.length > 0 && (
        <div className="preorder-filters" role="tablist" aria-label="Categories">
          <button
            type="button"
            role="tab"
            aria-selected={selectedCategory === ''}
            className={`preorder-chip${selectedCategory === '' ? ' active' : ''}`}
            onClick={() => setSelectedCategory('')}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={selectedCategory === cat}
              className={`preorder-chip${selectedCategory === cat ? ' active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {error ? (
        <div className="preorder-error" role="alert">
          {error}
        </div>
      ) : loading ? (
        <div className="preorder-grid">
          {Array.from({ length: PAGE_SIZE }).map((_, idx) => (
            <Skeleton key={idx} />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="preorder-empty">
          <h3>No products yet</h3>
          <p>
            {search || selectedCategory
              ? 'Try clearing the filters or search.'
              : 'Products created at this event will show up here as soon as they are published.'}
          </p>
        </div>
      ) : (
        <div className="preorder-grid">
          {products.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              bump={bumpedIds.has(p.id)}
              voting={votingIds.has(p.id)}
              onVote={handleVote}
            />
          ))}
          {loadingMore && (
            <div className="preorder-loading-more">Loading more products…</div>
          )}
          <div ref={sentinelRef} className="preorder-load-sentinel" aria-hidden />
        </div>
      )}

    </div>
  );
};

export default PreorderApp;
